import { InputType } from '@/constants/enums'
import { IntentLabels, DigitalIntentLabels, IntentHierarchyMapping, IntentHierarchyLabels } from '@/constants/strings'

/**
 * Get the display label for an intent. Non-shippable (digital) items get a
 * digital-specific label when one is defined; everything else falls back to
 * the standard IntentLabels map.
 */
export function getIntentLabel(item: any, intent: number): string {
  if (item && !item.is_shippable && intent in DigitalIntentLabels) {
    return DigitalIntentLabels[intent]
  }
  return (IntentLabels as Record<number, string>)[intent] || String(intent)
}

/**
 * Check if an intent is a "primary" intent (shown as a direct button, not nested in a popover).
 * Primary intents: ReactivateSubscription, RebillItem, OfferDetails
 */
export function isPrimaryIntent(intent: number): boolean {
  return [InputType.ReactivateSubscription, InputType.RebillItem, InputType.OfferDetails].includes(intent)
}

/**
 * Group intents by their hierarchy category.
 * Returns { categoryKey: { label: string, intents: number[] } }
 * Intents not in the hierarchy mapping are returned under a '_primary' key.
 */
export function groupIntentsByHierarchy(intents: number[]): Record<string, { label: string; intents: number[] }> {
  const groups: Record<string, { label: string; intents: number[] }> = {}
  const primary: number[] = []

  for (const intent of intents) {
    if (isPrimaryIntent(intent)) {
      primary.push(intent)
      continue
    }

    const category = IntentHierarchyMapping[intent]
    if (category) {
      if (!groups[category]) {
        groups[category] = {
          label: IntentHierarchyLabels[category] || category,
          intents: [],
        }
      }
      groups[category].intents.push(intent)
    } else {
      primary.push(intent)
    }
  }

  if (primary.length > 0) {
    groups._primary = { label: '', intents: primary }
  }

  return groups
}
